import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Info, Heart, AlertTriangle, CheckCircle } from 'lucide-react';

interface WellsCriterion {
  id: string;
  text: string;
  pointsWells1: number;
  pointsWells2: number;
  checked: boolean;
}

const Index = () => {
  const [isWells2, setIsWells2] = useState(false);
  const [criteria, setCriteria] = useState<WellsCriterion[]>([
    {
      id: 'dvt_signs',
      text: 'Klinische Zeichen für eine tiefe Beinvenenthrombose',
      pointsWells1: 3,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'alternative_unlikely',
      text: 'Andere Diagnosen sind unwahrscheinlich',
      pointsWells1: 3,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'heart_rate',
      text: 'Herzfrequenz > 100/min',
      pointsWells1: 1.5,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'immobilization',
      text: 'Immobilisation > 3 Tage oder OP vor < 4 Wochen',
      pointsWells1: 1.5,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'previous_pe_dvt',
      text: 'Frühere Lungenembolie oder tiefe Beinvenenthrombose',
      pointsWells1: 1.5,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'hemoptysis',
      text: 'Hämoptyse (Bluthusten)',
      pointsWells1: 1,
      pointsWells2: 1,
      checked: false
    },
    {
      id: 'malignancy',
      text: 'Neoplasie (aktive Krebserkrankung)',
      pointsWells1: 1,
      pointsWells2: 1,
      checked: false
    }
  ]);

  const handleCriterionChange = (id: string, checked: boolean) => {
    setCriteria(prev => prev.map(criterion => 
      criterion.id === id ? { ...criterion, checked } : criterion
    ));
  };

  const calculateScore = () => {
    return criteria.reduce((total, criterion) => {
      if (criterion.checked) {
        return total + (isWells2 ? criterion.pointsWells2 : criterion.pointsWells1);
      }
      return total;
    }, 0);
  };

  const getInterpretation = (score: number) => {
    if (isWells2) {
      if (score <= 1) {
        return {
          risk: 'Lungenembolie unwahrscheinlich',
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          icon: CheckCircle
        };
      } else {
        return {
          risk: 'Lungenembolie wahrscheinlich',
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: AlertTriangle
        };
      }
    } else {
      if (score < 2) {
        return {
          risk: 'Geringe Wahrscheinlichkeit',
          color: 'text-green-700',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          icon: CheckCircle
        };
      } else if (score <= 6) {
        return {
          risk: 'Mittlere Wahrscheinlichkeit',
          color: 'text-yellow-700',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          icon: AlertTriangle
        };
      } else {
        return {
          risk: 'Hohe Wahrscheinlichkeit',
          color: 'text-red-700',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          icon: AlertTriangle
        };
      }
    }
  };

  const score = calculateScore();
  const interpretation = getInterpretation(score);
  const IconComponent = interpretation.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Heart className="h-8 w-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Wells-Score Rechner</h1>
          </div>
          <p className="text-lg text-gray-600 mb-4">
            Klinische Einschätzung der Wahrscheinlichkeit einer Lungenembolie
          </p>
          
          {/* Version Toggle */}
          <div className="flex items-center justify-center gap-4 p-4 bg-white rounded-lg shadow-sm border">
            <span className={`font-medium ${!isWells2 ? 'text-primary' : 'text-gray-500'}`}>
              Wells-I Score
            </span>
            <Switch
              checked={isWells2}
              onCheckedChange={setIsWells2}
              className="data-[state=checked]:bg-primary"
            />
            <span className={`font-medium ${isWells2 ? 'text-primary' : 'text-gray-500'}`}>
              Wells-II Score
            </span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Criteria Card */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-0 bg-white">
              <CardHeader className="bg-gradient-to-r from-primary to-blue-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5" />
                  {isWells2 ? 'Wells-II Score Kriterien' : 'Wells-I Score Kriterien'}
                </CardTitle>
                <CardDescription className="text-blue-100">
                  Wählen Sie alle zutreffenden Kriterien aus
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {criteria.map((criterion) => (
                    <div key={criterion.id} className="flex items-start space-x-3 p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
                      <Checkbox
                        id={criterion.id}
                        checked={criterion.checked}
                        onCheckedChange={(checked) => handleCriterionChange(criterion.id, checked as boolean)}
                        className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={criterion.id}
                          className="text-sm font-medium text-gray-900 cursor-pointer leading-relaxed"
                        >
                          {criterion.text}
                        </label>
                        <div className="mt-1">
                          <Badge variant="outline" className="text-xs">
                            {isWells2 ? criterion.pointsWells2 : criterion.pointsWells1} 
                            {(isWells2 ? criterion.pointsWells2 : criterion.pointsWells1) === 1 ? 'Punkt' : 'Punkte'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results Card */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-0 bg-white sticky top-4">
              <CardHeader className="text-center">
                <CardTitle className="text-2xl font-bold text-gray-900">Ergebnis</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {/* Score Display */}
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold text-primary mb-2">
                    {score.toFixed(1)}
                  </div>
                  <div className="text-sm text-gray-600">
                    {score === 1 ? 'Punkt' : 'Punkte'}
                  </div>
                </div>

                {/* Interpretation */}
                <Alert className={`${interpretation.bgColor} ${interpretation.borderColor} border-2`}>
                  <IconComponent className={`h-5 w-5 ${interpretation.color}`} />
                  <AlertDescription className={`font-semibold ${interpretation.color}`}>
                    {interpretation.risk}
                  </AlertDescription>
                </Alert>

                {/* Score Ranges */}
                <div className="mt-6 space-y-2">
                  <h4 className="font-semibold text-gray-900 mb-3">
                    {isWells2 ? 'Wells-II Bewertung:' : 'Wells-I Bewertung:'}
                  </h4>
                  {isWells2 ? (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>0-1 Punkt:</span>
                        <span className="text-green-700 font-medium">Unwahrscheinlich</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>≥ 2 Punkte:</span>
                        <span className="text-red-700 font-medium">Wahrscheinlich</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between text-sm">
                        <span>&lt; 2 Punkte:</span>
                        <span className="text-green-700 font-medium">Gering</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>2-6 Punkte:</span>
                        <span className="text-yellow-700 font-medium">Mittel</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>≥ 7 Punkte:</span>
                        <span className="text-red-700 font-medium">Hoch</span>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-8 text-center">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Info className="h-4 w-4" />
                Weitere Informationen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Wells-Score für Lungenembolie
                </DialogTitle>
                <DialogDescription className="text-left space-y-4">
                  <p>
                    Der Wells-Score ist ein klinisches Bewertungssystem zur Einschätzung der 
                    Wahrscheinlichkeit einer Lungenembolie bei Patienten mit entsprechender Symptomatik.
                  </p>
                  <div>
                    <h4 className="font-semibold mb-2">Wells-I Score (Original):</h4>
                    <p className="text-sm">
                      Verwendet unterschiedliche Punktwerte (1, 1.5, 3 Punkte) und teilt in drei 
                      Risikogruppen ein: gering, mittel und hoch.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Wells-II Score (Vereinfacht):</h4>
                    <p className="text-sm">
                      Verwendet einheitlich 1 Punkt pro Kriterium und teilt in zwei Gruppen ein: 
                      wahrscheinlich oder unwahrscheinlich.
                    </p>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <p className="text-sm font-medium text-yellow-800">
                      <strong>Wichtiger Hinweis:</strong> Dieser Rechner dient nur zur Unterstützung 
                      der klinischen Entscheidungsfindung und ersetzt nicht die ärztliche Beurteilung.
                    </p>
                  </div>
                </DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-gray-500">
          <p>
            Basierend auf: Wells PS, et al. Derivation of a simple clinical model to categorize 
            patients probability of pulmonary embolism. Thromb Haemost. 2000.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default Index;
